import { Check, ChevronDown } from 'lucide-react';
import { createContext, type ReactNode, useContext } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentModelOption } from '@libraries/typescript/api_client';

const DEFAULT_AGENT_MODEL_ID = 'claude-opus-4-8';

export type AgentModelPickerProps = {
    readonly pinnedModel: string | null | undefined;
    readonly resolvedModel: string | null | undefined;
    readonly options: ReadonlyArray<AdminAgentModelOption> | undefined;
    readonly disabled?: boolean;
    readonly onModelChange: (model: string | null) => void;
};

type AgentModelPickerRenderer = (props: AgentModelPickerProps) => ReactNode;

// The native surface reuses the shared composer state, but owns its model
// picker UI. The provider lets that finished surface replace only the picker
// without adding a v3 import or branch to the legacy chat view.
const AgentModelPickerRendererContext =
    createContext<AgentModelPickerRenderer | null>(null);

export function AgentModelPickerRendererProvider({
    children,
    render,
}: {
    readonly children: ReactNode;
    readonly render: AgentModelPickerRenderer;
}) {
    return (
        <AgentModelPickerRendererContext.Provider value={render}>
            {children}
        </AgentModelPickerRendererContext.Provider>
    );
}

// Mirrors the curated server list; only rendered while the real options are
// still loading. Guarded by agent_model_options_parity.test.ts.
export const FALLBACK_AGENT_MODEL_OPTIONS: ReadonlyArray<AdminAgentModelOption> =
    [
        { id: 'claude-opus-5', label: 'Opus 5', provider: 'anthropic' },
        {
            id: DEFAULT_AGENT_MODEL_ID,
            label: 'Opus 4.8',
            provider: 'anthropic',
        },
        { id: 'claude-opus-4-7', label: 'Opus 4.7', provider: 'anthropic' },
        { id: 'claude-opus-4-6', label: 'Opus 4.6', provider: 'anthropic' },
        { id: 'claude-opus-4-5', label: 'Opus 4.5', provider: 'anthropic' },
        { id: 'claude-sonnet-5', label: 'Sonnet 5', provider: 'anthropic' },
        { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', provider: 'anthropic' },
        { id: 'claude-sonnet-4-5', label: 'Sonnet 4.5', provider: 'anthropic' },
        {
            id: 'claude-haiku-4-5-20251001',
            label: 'Haiku 4.5',
            provider: 'anthropic',
        },
        { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', provider: 'openai' },
        { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', provider: 'openai' },
        { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', provider: 'openai' },
        { id: 'gpt-5.5', label: 'GPT-5.5', provider: 'openai' },
        { id: 'gpt-5.4', label: 'GPT-5.4', provider: 'openai' },
        { id: 'gpt-5.2', label: 'GPT-5.2', provider: 'openai' },
    ];

function resolveOption(
    model: string,
    options: ReadonlyArray<AdminAgentModelOption>,
): AdminAgentModelOption {
    // Unlisted models (e.g. set via API) still get the right provider icon.
    return (
        options.find((option) => option.id === model) ?? {
            id: model,
            label: model,
            provider: model.startsWith('gpt-') ? 'openai' : 'anthropic',
        }
    );
}

function ModelIcon({
    provider,
    className,
}: {
    readonly provider: AdminAgentModelOption['provider'];
    readonly className?: string;
}) {
    return (
        <img
            src={provider === 'anthropic' ? '/anthropic.svg' : '/openai.svg'}
            alt=""
            aria-hidden={true}
            className={cn('size-3.5 shrink-0 object-contain', className)}
        />
    );
}

export function AgentModelPicker({
    pinnedModel,
    resolvedModel,
    options,
    disabled = false,
    onModelChange,
}: AgentModelPickerProps) {
    const renderOverride = useContext(AgentModelPickerRendererContext);
    if (renderOverride !== null) {
        return renderOverride({
            pinnedModel,
            resolvedModel,
            options,
            disabled,
            onModelChange,
        });
    }
    const modelOptions = options ?? FALLBACK_AGENT_MODEL_OPTIONS;
    // Before a run exists the server has not resolved anything, so claim
    // "Default" rather than a model the run may not use.
    const displayedModel = resolvedModel ?? pinnedModel ?? null;
    const displayedOption =
        displayedModel === null
            ? null
            : resolveOption(displayedModel, modelOptions);

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    className="bg-muted/70 hover:bg-muted h-7 max-w-full gap-1.5 rounded-full px-2.5 text-xs font-medium"
                >
                    {displayedOption === null ? null : (
                        <ModelIcon
                            provider={displayedOption.provider}
                            className="text-muted-foreground shrink-0"
                        />
                    )}
                    <span className="truncate">
                        {displayedOption?.label ?? 'Default'}
                    </span>
                    <ChevronDown className="text-muted-foreground size-3 shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72 p-2">
                <div className="text-muted-foreground px-2 py-2 text-sm font-medium">
                    Select a model
                </div>
                <DropdownMenuItem
                    disabled={disabled}
                    onClick={disabled ? undefined : () => onModelChange(null)}
                >
                    <span className="size-3.5 shrink-0" />
                    <span className="flex-1">Default</span>
                    {(pinnedModel ?? null) === null ? (
                        <Check className="size-4" />
                    ) : null}
                </DropdownMenuItem>
                {modelOptions.map((option) => (
                    <DropdownMenuItem
                        key={option.id}
                        disabled={disabled}
                        onClick={
                            disabled
                                ? undefined
                                : () => onModelChange(option.id)
                        }
                    >
                        <ModelIcon
                            provider={option.provider}
                            className="text-muted-foreground"
                        />
                        <span className="flex-1">{option.label}</span>
                        {pinnedModel === option.id ? (
                            <Check className="size-4" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
